import ErrorIcon from '@mui/icons-material/Error'
import { Box, Link, Typography } from '@mui/material'
import { MButton } from 'components'
import Stepper from 'components/ui/Stepper/Stepper'
import { ROUTES_PATH } from 'config'
import { color } from 'config/color'
import type React from 'react'
import { useState } from 'react'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import FormStatus from './FormStatus'
import InformationForm from './InformationForm'
import ReviewInformationForm from './ReviewInformationForm'
import SearchAddressForm from './SearchAddressForm'
import SelectAddressForm from './SelectAddressForm'
import SelectServiceAddressForm from './SelectServiceAddressForm'
import UserValidationForm from './UserValidationForm'

interface ServiceFormProps {
    formType: string
    title: string
    desc: string
    handleClick: () => void
    isAuthenticated: boolean
}

export interface InformationFormType {
    name?: string
    birthDate?: string
    phone?: string
    email?: string
    comments?: string
    startDate?: string
    accountType: string
    ownershipType: string
    stopDate?: string
    mailingAddressLine1?: string
    mailingAddressLine2?: string
    mailingCity?: string
    mailingState?: string
    mailingZipCode?: string
    selectedAddress?: string
    svcAddressLine1?: string
    svcAddressLine2?: string
    svcAddressCity?: string
    svcAddressState?: string
    svcAddressZipCode?: string
    billingPref?: string
    mailingPref?: string
    accountId?: string
    lastName?: string
    state?: string
    ein?: string
    driversLicense?: string
}


export interface ErrorMessageType {
    title: string
    description: string
}

export interface PremiseType {
    premiseId: string
    premiseInfo: string
}

interface StepType {
    title: string
    description: string
    component: JSX.Element
}

export enum FormType {
    Start = 'Start',
    Stop = 'Stop',
    Transfer = 'Transfer',
    NewTap = 'New Tap'
}

const ServiceForm: React.FC<ServiceFormProps> = ({ title, desc, handleClick, formType, isAuthenticated }) => {
    const isMobile = useMobileBreakpoints()
    const [activeStep, setActiveStep] = useState<number>(0)
    const [formData, setFormData] = useState<InformationFormType>({
        name: '',
        birthDate: '',
        phone: '',
        email: '',
        comments: '',
        startDate: '',
        accountType: '',
        ownershipType: '',
        stopDate: '',
        mailingAddressLine1: '',
        mailingAddressLine2: '',
        mailingCity: '',
        mailingState: '',
        mailingZipCode: '',
        selectedAddress: '',
        accountId: '',
        lastName: '',
        ein: '',
        driversLicense: ''
    })
    const [errorMessage, setErrorMessage] = useState<ErrorMessageType>()
    const [premiseList, setPremiseList] = useState<PremiseType[]>()
    const [formStatus, setFormStatus] = useState<boolean>(false)

    const handleNextStep = (): void => {
        setActiveStep((prev) => prev + 1)
        setErrorMessage(undefined)
    }

    const handlePrevStep = (): void => {
        if (activeStep === 0) {
            handleClick()
        } else {
            setActiveStep((prev) => prev - 1)
        }
    }


    const steps: StepType[] = [
        ...(isAuthenticated || formType === FormType.NewTap || (!isAuthenticated && formType === FormType.Start)
            ? []
            : [{
                title: 'Register',
                description: 'Enter your account information to which you want to associate this request.',
                component: <UserValidationForm handleNext={handleNextStep} handleBack={handlePrevStep} formData={formData} setFormData={setFormData} />
            }]
        ),
        ...(formType === FormType.Start
            ? [
                {
                    title: 'Address for new service',
                    description: 'Provide the details for the new service request to move forward.',
                    component: <SearchAddressForm handleNext={handleNextStep} handleBack={handlePrevStep} setErrorMessage={setErrorMessage} setPremiseList={setPremiseList} />
                },
                {
                    title: 'Select an address',
                    description: 'Choose the address where you would like this service to be provided.',
                    component: <SelectAddressForm handleNext={handleNextStep} handleBack={handlePrevStep} formData={formData} setFormData={setFormData} premiseList={premiseList} />
                },
                {
                    title: 'Enter your information',
                    description: 'Enter the requested information to proceed with your request.',
                    component: <InformationForm formType={formType} handleNext={handleNextStep} handleBack={handlePrevStep} formData={formData} setFormData={setFormData} />
                },
                {
                    title: 'Review your information',
                    description: "Review all the details you've entered. Once submitted, we'll start processing your request.",
                    component: <ReviewInformationForm handleNext={handleNextStep} handleBack={handlePrevStep} formData={formData} setActiveStep={setActiveStep} formType={formType} setFormStatus={setFormStatus} />
                }
            ]
            : []),
        ...(formType === FormType.Stop
            ? [
                {
                    title: 'Select an address',
                    description: 'Choose the address where you would like this service to be provided.',
                    component: <SelectServiceAddressForm formType={formType} handleNext={handleNextStep} handleBack={handlePrevStep} formData={formData} setFormData={setFormData} />
                },
                {
                    title: 'Enter your information',
                    description: 'Enter the requested information to proceed with your request.',
                    component: <InformationForm formType={formType} handleNext={handleNextStep} handleBack={handlePrevStep} formData={formData} setFormData={setFormData} />
                },
                {
                    title: 'Review your information',
                    description: 'Review all the provided information before submission. Once submitted, we will process your request.',
                    component: <ReviewInformationForm handleNext={handleNextStep} handleBack={handlePrevStep} formData={formData} setActiveStep={setActiveStep} formType={formType} setFormStatus={setFormStatus} />
                }
            ]
            : []),
        ...(formType === FormType.Transfer
            ? [
                {
                    title: 'Select an old address',
                    description: 'Choose the address where the service is currently provided.',
                    component: <SelectServiceAddressForm formType={formType} handleNext={handleNextStep} handleBack={handlePrevStep} formData={formData} setFormData={setFormData} />
                },
                {
                    title: 'Address for new service',
                    description: 'Provide the details for the new service request to move forward.',
                    component: <SearchAddressForm handleNext={handleNextStep} handleBack={handlePrevStep} setErrorMessage={setErrorMessage} setPremiseList={setPremiseList} />
                },
                {
                    title: 'Select a new address',
                    description: 'Choose the address where you would like the service to be transferred.',
                    component: <SelectAddressForm handleNext={handleNextStep} handleBack={handlePrevStep} formData={formData} setFormData={setFormData} premiseList={premiseList} />
                },
                {
                    title: 'Enter your information',
                    description: 'Enter the requested information to proceed with your request.',
                    component: <InformationForm formType={formType} handleNext={handleNextStep} handleBack={handlePrevStep} formData={formData} setFormData={setFormData} />
                },
                {
                    title: 'Review your information',
                    description: 'Review all the provided information before submission. Once submitted, we will process your request.',
                    component: <ReviewInformationForm handleNext={handleNextStep} handleBack={handlePrevStep} formData={formData} setActiveStep={setActiveStep} formType={formType} setFormStatus={setFormStatus} />
                }
            ]
            : []),
        ...(formType === FormType.NewTap
            ? [
                {
                    title: 'Enter your information',
                    description: 'Enter the requested information to proceed with your request.',
                    component: <InformationForm formType={formType} handleNext={handleNextStep} handleBack={handlePrevStep} formData={formData} setFormData={setFormData} />
                },
                {
                    title: 'Review your information',
                    description: "Review all the details you've entered. Once submitted, we'll start processing your request.",
                    component: <ReviewInformationForm handleNext={handleNextStep} handleBack={handlePrevStep} formData={formData} setActiveStep={setActiveStep} formType={formType} setFormStatus={setFormStatus} />
                }
            ]
            : []),
        {
            title: '',
            description: '',
            component: <FormStatus formType={formType} status={formStatus} backToMenu={handleClick} />
        }
    ]

    return (
        <>
            <Box p={isMobile ? 3 : 5} display="flex" flexDirection={isMobile ? 'column' : 'row'} alignItems="center" justifyContent="space-between" gap={2} borderBottom={`solid 1px ${color.background[200]}`}>
                <Box width={isMobile ? '100%' : '75%'}>
                    <Typography fontSize={isMobile ? 18 : 24} fontWeight={600} color={color.grey[900]} mb={1}>
                        {title}
                    </Typography>
                    <Typography fontSize={14} color={color.grey[700]}>
                        {desc + ' '}
                        If you have any questions, feel free to <Link href={ROUTES_PATH.CONTACT_US}>contact us</Link>.
                    </Typography>
                </Box>
                <MButton variant="outlined" rounded="true" texttransform="none" onClick={handleClick}>
                    Go back to Services
                </MButton>
            </Box>
            <Box p={isMobile ? 3 : 5} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                {activeStep !== (steps.length - 1) && (
                    <Box width="75%" bgcolor={color.grey[50]} p={3} border={`solid 1px ${color.background[100]}`} borderRadius={1}>
                        <Box display="flex">
                            {(errorMessage != null) && <ErrorIcon sx={{ color: color.grey[400], fontSize: 24, mr: 0.5 }} />}
                            <Typography fontSize={18} fontWeight={600}>
                                {(errorMessage != null) ? errorMessage.title : `Step ${activeStep + 1}: ${steps[activeStep]?.title}`}
                            </Typography>
                        </Box>
                        <Typography fontSize={14}>{(errorMessage != null) ? errorMessage.description : steps[activeStep]?.description}</Typography>
                        <Stepper errorMessage={errorMessage} numOfSteps={steps.length} activeStep={activeStep} />
                    </Box>
                )}
                <Box width="80%" mt={5}>
                    {steps[activeStep].component}
                </Box>
            </Box>
        </>
    )
}

export default ServiceForm
